import React, { useContext, useEffect, useRef, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import {
  EditOfferPageContext,
  PreviewDevice,
} from '@bbkAdminComponents/experiences/offers/edit-offer-page-context';
import Checkbox, {
  CheckboxLabel,
} from '@bbkAdminComponents/switches/checkbox/checkbox-v2';
import {
  type RestModalCard,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import ActionButtonIcons from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { useMediaQueries } from '@bbkFunnelComponents/modals/sections/media-queries';
import { OtherActions } from '@bbkFunnelComponents/modals/sections/other-actions';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import { type ActionData } from '../v2';

type Props = {
  onClick: (action: RestModalForwards) => void;
  fullModalInfo: RestModalCard;
  hide?: () => void;
  version2Template?: boolean;
  actionData?: ActionData;
};

const ModalSimpleActions: React.FC<Props> = ({
  onClick,
  hide,
  fullModalInfo,
  actionData,
  version2Template,
}) => {
  const id = actionData ? actionData.id : fullModalInfo.id;
  const includeOr = actionData
    ? actionData.includeOr
    : fullModalInfo.requires.includeOr;
  const includeOrText = actionData
    ? actionData.includeOrText
    : fullModalInfo.requires.includeOrText;
  const info = actionData
    ? actionData.forwards
    : fullModalInfo.requires.forwards;
  const otherActions = actionData
    ? actionData.other_actions
    : fullModalInfo.requires.other_actions;

  const funnelLabelStyle = {
    display: 'block',
    paddingLeft: '5px',
    paddingRight: '5px',
  };
  if (info.icon) {
    funnelLabelStyle.display = 'inline-block';
  }
  const theme = useTheme() as EmotionTheme;
  const { mobileView } = useMediaQueries();

  let labelText: React.ReactNode = info.text;
  if (info.subText) {
    labelText = (
      <span>
        <span>{info.text}</span>
        <span style={{ display: 'block', fontWeight: 'lighter' }}>
          {info.subText}
        </span>
      </span>
    );
  }

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const mainBtn = useRef<HTMLButtonElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, mainBtn), [mainBtn]);

  const equalButtonSize = theme.complianceEqualButtonSize;
  const offerContext = useContext(EditOfferPageContext);
  const isPreview = !!offerContext;
  const isMobilePreview =
    isPreview && offerContext?.previewDevice === PreviewDevice.MOBILE;

  // Consent checkbox state
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const showConsent = info?.consent?.enabled === true;
  const requireExplicitConsent = info?.consent?.requireExplicit === true;
  const consentMessage = info?.consent?.message || '';

  const handleActionClick = () => {
    // Only require checkbox if explicit consent is enabled
    if (showConsent && requireExplicitConsent && !consentChecked) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    onClick(info);
  };

  // Reset error when consent is checked
  useEffect(() => {
    if (consentChecked) {
      setConsentError(false);
    }
  }, [consentChecked]);

  // For equal size button, gap is 25px one side for all buttons so added 11px to make it equal
  return (
    <div
      className={cs('modal-simple-actions', {
        'tw-flex': equalButtonSize && !mobileView && !isMobilePreview,
        'tw-px-[11px]': equalButtonSize && (mobileView || isMobilePreview),
      })}
    >
      <div className="MSAContainer">
        {showConsent && (
          <div className="tw-mb-3">
            {requireExplicitConsent ? (
              <CheckboxLabel className="!tw-items-start">
                <Checkbox
                  css={css`
                    ${
                      consentError &&
                      css`
                        input + div {
                          border-color: ${theme.errorVal};
                          box-shadow: 0 0 3px ${theme.errorVal}40;
                        }
                      `
                    }
                  `}
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <CustomerHtml value={consentMessage} />
              </CheckboxLabel>
            ) : (
              <CustomerHtml value={consentMessage} />
            )}
          </div>
        )}
        <div className="es-btn">
          <FunnelButton
            ref={mainBtn}
            onClick={handleActionClick}
            specificStyles={{ width: '100%' }}
            classes="d-flex align-items-center justify-content-center"
          >
            {info?.icon && <ActionButtonIcons icon={info.icon} />}
            <span style={funnelLabelStyle}>{labelText}</span>
          </FunnelButton>
        </div>
        {otherActions && otherActions.length > 0 && (
          <OtherActions
            id={id}
            otherActions={otherActions}
            onClick={onClick}
            includeOr={includeOr}
            includeOrText={includeOrText}
            hide={hide}
            version2Template={version2Template}
          />
        )}
      </div>
    </div>
  );
};

export default ModalSimpleActions;
